import React, { ReactNode, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import noop from 'lib/function/noop'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import { containerLarge } from 'styles/vendor/bootstrap/variables'
import useHorizontalScrollIntoView from 'hooks/useHorizontalScrollIntoView'

const TabOption = styled(Clickable)`
  height: ${rem(48)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(16)};
  min-width: ${rem(96)};

  ${mediaQueryUp.tablet} {
    height: ${rem(56)};
  }
`

const TabOptionSelectedLine = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  height: ${rem(3)};
  width: 100%;
  background-color: ${props => props.theme.palette.brand.primary.normal};
  display: none;

  &.active {
    display: block;
  }
`

const Wrapper = styled.div`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  position: sticky;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 1;
  top: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

`

const TabOptionText = styled(BodyText)`
  transition: color 0.2s;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`

const MenuBar = styled.div`
  display: inline-flex;
  white-space: nowrap;
`

const Nav = styled.nav`
  max-width: ${containerLarge};
  margin: 0 auto;
`

interface ContentFields {
  id: string;
  label: string | ReactNode;
}
interface Props {
  contentFields: Array<ContentFields>;
  onClick?: (id: string) => void;
  selectedHeader?: string;
}

const NavigableOfferContentBar = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { contentFields, onClick = noop, selectedHeader } = props
  const [scroller, contentElements] = useHorizontalScrollIntoView(selectedHeader)
  useImperativeHandle(ref, () => scroller.current as HTMLDivElement)

  return (
    <Wrapper ref={scroller}>
      <Nav>
        <MenuBar role="menubar">
          {contentFields.map(contentField => {
            return <TabOption
              ref={(element) => { contentElements.current[contentField.id] = element as HTMLButtonElement }}
              key={contentField.id}
              onClick={() => onClick(contentField.id)}
              role="menuitem"
            >
              <TabOptionText
                wrap="no-wrap"
                colour={contentField.id === selectedHeader ? 'primary' : undefined}
                variant="medium"
              >
                {contentField.label}
                <TabOptionSelectedLine className={cn({ active: contentField.id === selectedHeader })} />
              </TabOptionText>
            </TabOption>
          })}
        </MenuBar>
      </Nav>
    </Wrapper>
  )
})

NavigableOfferContentBar.displayName = 'NavigableOfferContentBar'

export default NavigableOfferContentBar
